/* iter55k — Phase 5: Coaching booking dialog.
 *
 * Flow:
 *   1. Fetch quota + open slots on open.
 *   2. If quota already used this month, show a "come back next month" state.
 *   3. Otherwise render a slot picker + notes textarea + submit button.
 *   4. Post-book, show a confirmation card with the booked time.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { humanServicesApi } from '@/lib/api';
import { toast } from 'sonner';
import { Users, Calendar, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric',
                                          hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

export function CoachingBookDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [quota, setQuota]     = useState(null);
  const [slots, setSlots]     = useState([]);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [notes, setNotes]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [booked, setBooked]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        humanServicesApi.quota(),
        humanServicesApi.coachingSlots(),
      ]);
      setQuota(q);
      setSlots(s.slots || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'upgrade_required') {
        toast.info('Available on Premier Studio', {
          description: 'Upgrade in Settings → Subscription to book 1:1 coaching.',
        });
        onOpenChange(false);
      } else {
        toast.error('Failed to load coaching slots.');
      }
    } finally {
      setLoading(false);
    }
  }, [onOpenChange]);

  useEffect(() => {
    if (open) {
      setBooked(null);
      setPickedSlot(null);
      setNotes('');
      load();
    }
  }, [open, load]);

  const submit = async () => {
    if (!pickedSlot) return;
    setBusy(true);
    try {
      const r = await humanServicesApi.coachingBook(pickedSlot, notes);
      setBooked(r.session);
      toast.success('Coaching session booked.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Booking failed.');
    } finally {
      setBusy(false);
    }
  };

  const alreadyUsed = quota?.coaching?.used >= (quota?.coaching?.limit || 0)
                     && (quota?.coaching?.limit || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="coaching-book-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-[14px]">
            <Users className="h-4 w-4 text-primary" /> Book a coaching session
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Premier Studio includes one 1:1 call per calendar month with a professional novelist.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-[12px] text-muted-foreground">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            <div className="mt-2">Loading availability…</div>
          </div>
        ) : booked ? (
          <BookedConfirmation session={booked} onClose={() => onOpenChange(false)} />
        ) : alreadyUsed ? (
          <QuotaExhaustedState monthKey={quota.month_key}
                                 kind="coaching"
                                 onClose={() => onOpenChange(false)} />
        ) : (
          <>
            <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1"
                 data-testid="coaching-slot-list">
              {slots.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground">
                  No slots open yet. Please check back — the coach opens new
                  windows every Sunday for the coming week.
                </div>
              ) : slots.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setPickedSlot(s.id)}
                  data-testid={`coaching-slot-${s.id}`}
                  data-active={pickedSlot === s.id ? '1' : '0'}
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-[12px] transition-colors ${
                    pickedSlot === s.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/60 hover:bg-secondary'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    {fmtTime(s.start_ts)}
                  </span>
                  <span className="text-[10.5px] text-muted-foreground">
                    {s.duration_minutes} min
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-3">
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                What would you like to focus on? (optional)
              </label>
              <Textarea
                data-testid="coaching-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Pacing help for the mid-act sag in my thriller."
                className="h-20 text-[12px]"
                maxLength={2000}
              />
            </div>

            <DialogFooter>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}
                      data-testid="coaching-cancel-btn">
                Cancel
              </Button>
              <Button size="sm"
                      onClick={submit}
                      disabled={!pickedSlot || busy}
                      data-testid="coaching-submit-btn">
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />}
                Book slot
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BookedConfirmation({ session, onClose }) {
  return (
    <div className="py-4 text-center" data-testid="coaching-booked-confirmation">
      <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
      <div className="mt-3 text-[14px] font-semibold">You&apos;re booked!</div>
      <div className="mt-1 text-[12px] text-muted-foreground">
        {fmtTime(session.start_ts)}
      </div>
      <p className="mt-3 text-[11.5px] text-muted-foreground">
        A calendar invite will land in your inbox shortly. Reply to that email
        with any prep material you&apos;d like the coach to read.
      </p>
      <div className="mt-4 flex justify-center">
        <Button size="sm" onClick={onClose} data-testid="coaching-close-confirmation-btn">Done</Button>
      </div>
    </div>
  );
}

export function QuotaExhaustedState({ monthKey, kind, onClose }) {
  const label = kind === 'coaching' ? 'coaching' : 'editor review';
  return (
    <div className="py-4 text-center" data-testid={`quota-exhausted-${kind}`}>
      <AlertTriangle className="mx-auto h-6 w-6 text-amber-500" />
      <div className="mt-2 text-[13px] font-semibold">
        You&apos;ve used your {label} for {monthKey}.
      </div>
      <p className="mt-2 text-[11.5px] text-muted-foreground">
        Premier Studio includes one {label} per calendar month. Come back on
        the first of next month to book again.
      </p>
      <div className="mt-4 flex justify-center">
        <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}
